import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface FormValues {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { isSubmitting } } = useForm<FormValues>();

  async function onSubmit(values: FormValues) {
    setError(null);
    try {
      await login(values.email.trim().toLowerCase(), values.password);
      navigate('/dashboard');
    } catch {
      setError(t('auth.invalid_credentials'));
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="absolute top-4 end-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 brand-gradient h-12 w-12 rounded-xl" />
          <CardTitle className="text-2xl">{t('auth.signin_title')}</CardTitle>
          <CardDescription>{t('auth.signin_subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                {...register('email', { required: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                {...register('password', { required: true })}
              />
            </div>
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.submitting') : t('auth.submit')}
            </Button>
            <p className="text-xs text-center text-muted-foreground pt-2">
              {t('auth.signup_hint', { defaultValue: "Don't have an account?" })}{' '}
              <Link to="/signup" className="text-primary hover:underline font-medium">
                {t('auth.signup_link', { defaultValue: 'Create a workspace' })}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
